import { useLocalPreviewVideoUrl } from '@src/hooks/useLocalPreviewVideoUrl';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import ForwardToEndIcon from '@src/svgs/ForwardToEndIcon';
import PlayForwardIcon from '@src/svgs/PlayForwardIcon';
import { isCompressedRenditionPending } from '@src/utility/storyReadiness';
import { convertTimeToMMSS } from '@src/utility/timeFormat';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Tooltip } from 'react-tooltip';
import styled from 'styled-components';

import {
  getBeatKey,
  getStoryThumbnailUrl,
  resolveVideoSrc,
} from './clipSequenceUtils';
import { useReelBeatReorder } from './useReelBeatReorder';

import type { ReelOutline } from './types';
import type { ReelClipResults } from '@src/services/reelClipProducer/types';

type Segment = {
  beatKey: string;
  title: string;
  text: string;
  speaker: string | null;
  startTime: number;
  endTime: number;
  duration: number;
  chapterTitle: string;
  chapterIndex: number;
  beatIndex: number;
  thumbnailUrl: string;
};

type ReelPreviewPlayerProps = {
  outline: ReelOutline;
  clipResults: ReelClipResults;
  excludedBeats: Set<string>;
  onOutlineChange: (outline: ReelOutline) => void;
};

const ReelPreviewPlayer: React.FC<ReelPreviewPlayerProps> = ({
  outline,
  clipResults,
  excludedBeats,
  onOutlineChange,
}) => {
  const videoCreator = useVideoCreatorStore();
  const playerRef = useRef<HTMLVideoElement>(null);
  const [currentSegmentIndex, setCurrentSegmentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);

  const {
    chapterDragKey,
    chapterDragOverKey,
    handleChapterDragStart,
    handleChapterDragOver,
    handleChapterDragLeave,
    handleChapterDrop,
    handleChapterDragEnd,
    beatDragKey,
    beatDragOverKey,
    handleBeatDragStart,
    handleBeatDragOver,
    handleBeatDragLeave,
    handleBeatDrop,
    handleBeatDragEnd,
  } = useReelBeatReorder(outline, onOutlineChange);

  // PLAT-11445 (audio-first): while renditions are pending there is no mp4
  // rendition to preview from and no story thumbnails yet — play the session's
  // local upload when we hold it, and skip thumbnails rather than request
  // URLs that would render as broken images.
  const renditionPending = isCompressedRenditionPending(videoCreator.story);
  const localPreviewUrl = useLocalPreviewVideoUrl(videoCreator.story?.id);
  const videoSrc = useMemo(
    () =>
      resolveVideoSrc(videoCreator.story) ||
      (renditionPending ? (localPreviewUrl ?? '') : ''),
    [videoCreator.story, renditionPending, localPreviewUrl],
  );

  const getThumbnailUrl = useCallback(
    (timecode?: number): string =>
      renditionPending
        ? ''
        : getStoryThumbnailUrl(videoCreator.story, timecode),
    [videoCreator.story, renditionPending],
  );

  const segments: Segment[] = useMemo(() => {
    const result: Segment[] = [];
    let cumulativeOffset = 0;

    outline.chapters.forEach((chapter, chapterIdx) => {
      chapter.beats.forEach((beat, beatIdx) => {
        const key = getBeatKey(chapter, beat);
        const beatResult = clipResults.get(key);
        const selected = beatResult
          ? beatResult.options[beatResult.selectedOptionIndex]
          : null;
        const clip = selected?.clipFragment;

        const startTime = clip?.startTime ?? cumulativeOffset;
        const duration = clip?.duration ?? beat.estimatedDuration;

        cumulativeOffset += beat.estimatedDuration;

        if (excludedBeats.has(key)) return;

        if (duration > 0) {
          result.push({
            beatKey: key,
            title: selected?.title || beat.shortTitle || beat.description,
            text: selected?.text || beat.description,
            speaker: clip?.speakerName || beat.speaker || null,
            startTime,
            endTime: startTime + duration,
            duration,
            chapterTitle: chapter.title,
            chapterIndex: chapterIdx,
            beatIndex: beatIdx,
            thumbnailUrl: getThumbnailUrl(startTime),
          });
        }
      });
    });
    return result;
  }, [outline, clipResults, excludedBeats, getThumbnailUrl]);

  const totalDuration = useMemo(
    () => segments.reduce((sum, s) => sum + s.duration, 0),
    [segments],
  );

  const currentSegment = segments[currentSegmentIndex] ?? null;

  const seekToSegment = useCallback(
    (index: number) => {
      if (index < 0 || index >= segments.length) return;
      const seg = segments[index];
      setCurrentSegmentIndex(index);
      const player = playerRef.current;
      if (player) {
        player.currentTime = seg.startTime;
        player.play?.();
        setIsPlaying(true);
      }
    },
    [segments],
  );

  const handleTimeUpdate = useCallback(() => {
    const player = playerRef.current;
    if (!player) return;

    const time = player.currentTime ?? 0;
    setCurrentTime(time);

    if (currentSegment && time >= currentSegment.endTime - 0.1) {
      const nextIdx = currentSegmentIndex + 1;
      if (nextIdx < segments.length) {
        seekToSegment(nextIdx);
      } else {
        player.pause?.();
        setIsPlaying(false);
      }
    }
  }, [currentSegment, currentSegmentIndex, segments, seekToSegment]);

  const handlePlayPause = useCallback(() => {
    const player = playerRef.current;
    if (!player) return;

    if (isPlaying) {
      player.pause?.();
      setIsPlaying(false);
    } else {
      if (segments.length > 0 && currentSegment) {
        player.play?.();
        setIsPlaying(true);
      }
    }
  }, [isPlaying, segments, currentSegment]);

  const handlePlayFromStart = useCallback(() => {
    if (segments.length > 0) {
      seekToSegment(0);
    }
  }, [segments, seekToSegment]);

  const handlePrevChapter = useCallback(() => {
    if (segments.length === 0) return;
    const curChapter = currentSegment?.chapterIndex ?? 0;
    // Find the first segment of the previous chapter
    for (let i = currentSegmentIndex - 1; i >= 0; i--) {
      if (segments[i].chapterIndex < curChapter) {
        // Find the first segment of that chapter
        const targetChapter = segments[i].chapterIndex;
        const firstInChapter = segments.findIndex(
          s => s.chapterIndex === targetChapter,
        );
        if (firstInChapter >= 0) {
          seekToSegment(firstInChapter);
          return;
        }
      }
    }
    // Already at first chapter — go to start
    seekToSegment(0);
  }, [segments, currentSegmentIndex, currentSegment, seekToSegment]);

  const handleNextChapter = useCallback(() => {
    if (segments.length === 0) return;
    const curChapter = currentSegment?.chapterIndex ?? 0;
    // Find the first segment of the next chapter
    for (let i = currentSegmentIndex + 1; i < segments.length; i++) {
      if (segments[i].chapterIndex > curChapter) {
        seekToSegment(i);
        return;
      }
    }
  }, [segments, currentSegmentIndex, currentSegment, seekToSegment]);

  useEffect(() => {
    if (segments.length > 0 && playerRef.current) {
      playerRef.current.currentTime = segments[0].startTime;
    }
  }, [segments]);

  const elapsedTime = useMemo(() => {
    let elapsed = 0;
    for (let i = 0; i < currentSegmentIndex; i++) {
      elapsed += segments[i].duration;
    }
    if (currentSegment && currentTime >= currentSegment.startTime) {
      elapsed += Math.min(
        currentTime - currentSegment.startTime,
        currentSegment.duration,
      );
    }
    return elapsed;
  }, [currentSegmentIndex, currentSegment, currentTime, segments]);

  const progressPercent =
    totalDuration > 0 ? (elapsedTime / totalDuration) * 100 : 0;

  const hasClips = segments.length > 0;

  const chapterGroups = useMemo(() => {
    const groups: {
      title: string;
      segments: { seg: Segment; globalIdx: number }[];
    }[] = [];
    let currentChapterIdx = -1;

    segments.forEach((seg, idx) => {
      if (seg.chapterIndex !== currentChapterIdx) {
        groups.push({ title: seg.chapterTitle, segments: [] });
        currentChapterIdx = seg.chapterIndex;
      }
      groups[groups.length - 1].segments.push({ seg, globalIdx: idx });
    });

    return groups;
  }, [segments]);

  // Handle click on the progress bar to seek
  const handleProgressClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!hasClips || totalDuration === 0) return;
      const rect = e.currentTarget.getBoundingClientRect();
      const clickPercent = (e.clientX - rect.left) / rect.width;
      const targetElapsed = clickPercent * totalDuration;

      // Find which segment this falls in
      let cumulative = 0;
      for (let i = 0; i < segments.length; i++) {
        if (cumulative + segments[i].duration >= targetElapsed) {
          const offsetInSegment = targetElapsed - cumulative;
          setCurrentSegmentIndex(i);
          const player = playerRef.current;
          if (player) {
            player.currentTime = segments[i].startTime + offsetInSegment;
          }
          return;
        }
        cumulative += segments[i].duration;
      }
    },
    [hasClips, totalDuration, segments],
  );

  return (
    <Container>
      <VideoSection>
        <VideoWrapper onClick={handlePlayPause}>
          {videoSrc ? (
            <StyledVideo
              ref={playerRef}
              src={videoSrc}
              onTimeUpdate={handleTimeUpdate}
              onPlay={() => setIsPlaying(true)}
              onPause={() => setIsPlaying(false)}
              preload="auto"
              disableRemotePlayback
              playsInline
            />
          ) : (
            <NoVideoMessage>No video source available</NoVideoMessage>
          )}
        </VideoWrapper>

        {/* Clip info overlay at top of video */}
        {currentSegment && (
          <ClipInfoOverlay>
            <ClipInfoHeader>
              <ClipInfoTitle>{currentSegment.title}</ClipInfoTitle>
              <ClipInfoTime>
                {convertTimeToMMSS(Math.round(currentSegment.startTime))} -{' '}
                {convertTimeToMMSS(Math.round(currentSegment.endTime))}
                {currentSegment.speaker && ` · ${currentSegment.speaker}`}
              </ClipInfoTime>
            </ClipInfoHeader>
            <ClipInfoTranscript>{currentSegment.text}</ClipInfoTranscript>
          </ClipInfoOverlay>
        )}

        {/* Overlaid controls at bottom of video */}
        <VideoOverlayControls>
          <OverlayControlsRow>
            <ControlButton
              onClick={handlePlayFromStart}
              disabled={!hasClips}
              title="Restart"
            >
              <ControlBackward>
                <ForwardToEndIcon width="14" height="14" stroke="#fff" />
              </ControlBackward>
            </ControlButton>
            <ControlButton
              onClick={handlePrevChapter}
              disabled={!hasClips}
              title="Previous chapter"
            >
              <ControlBackward>
                <PlayForwardIcon width="16" height="10" fill="#fff" />
              </ControlBackward>
            </ControlButton>
            <PlayPauseButton onClick={handlePlayPause} disabled={!hasClips}>
              {isPlaying ? (
                <svg
                  viewBox="0 0 24 24"
                  width="28"
                  height="28"
                  fill="var(--foreground)"
                >
                  <path d="M14,19H18V5H14M6,19H10V5H6V19Z" />
                </svg>
              ) : (
                <svg
                  viewBox="0 0 24 24"
                  fill="var(--foreground)"
                  width="28"
                  height="28"
                >
                  <path
                    fillRule="evenodd"
                    d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12zm14.024-.983a1.125 1.125 0 010 1.966l-5.603 3.113A1.125 1.125 0 019 15.113V8.887c0-.857.921-1.4 1.671-.983l5.603 3.113z"
                    clipRule="evenodd"
                  />
                </svg>
              )}
            </PlayPauseButton>
            <ControlButton
              onClick={handleNextChapter}
              disabled={!hasClips}
              title="Next chapter"
            >
              <PlayForwardIcon width="16" height="10" fill="#fff" />
            </ControlButton>
            <ControlButton
              onClick={() => seekToSegment(segments.length - 1)}
              disabled={!hasClips}
              title="Go to end"
            >
              <ForwardToEndIcon width="14" height="14" stroke="#fff" />
            </ControlButton>
            <TimeDisplay>
              {hasClips
                ? `${convertTimeToMMSS(Math.floor(elapsedTime))} / ${convertTimeToMMSS(Math.floor(totalDuration))}`
                : 'No clips loaded'}
            </TimeDisplay>
            {currentSegment && (
              <NowPlaying>
                {currentSegment.title}
                {currentSegment.speaker && ` — ${currentSegment.speaker}`}
              </NowPlaying>
            )}
          </OverlayControlsRow>

          {/* Progress bar */}
          <ProgressBar onClick={handleProgressClick}>
            <ProgressFill style={{ width: `${progressPercent}%` }} />
          </ProgressBar>

          {/* Beat timeline */}
          <BeatTimeline>
            {segments.map((seg, idx) => {
              const widthPercent = (seg.duration / totalDuration) * 100;
              const isActive = idx === currentSegmentIndex;
              const showChapterDivider =
                idx > 0 && seg.chapterIndex !== segments[idx - 1].chapterIndex;

              const beatDragId = `${seg.chapterIndex}.${seg.beatIndex}`;
              const isDragging = beatDragKey === beatDragId;
              const isDragOver = beatDragOverKey === beatDragId;

              return (
                <React.Fragment key={seg.beatKey}>
                  {showChapterDivider && <ChapterDivider />}
                  <BeatBox
                    $widthPercent={widthPercent}
                    $active={isActive}
                    $isDragging={isDragging}
                    $isDragOver={isDragOver}
                    draggable
                    onClick={() => seekToSegment(idx)}
                    onDragStart={e =>
                      handleBeatDragStart(e, seg.chapterIndex, seg.beatIndex)
                    }
                    onDragOver={e =>
                      handleBeatDragOver(e, seg.chapterIndex, seg.beatIndex)
                    }
                    onDragLeave={handleBeatDragLeave}
                    onDrop={e =>
                      handleBeatDrop(e, seg.chapterIndex, seg.beatIndex)
                    }
                    onDragEnd={handleBeatDragEnd}
                    data-tooltip-id="preview-tooltip"
                    data-tooltip-content={seg.text}
                  >
                    <BeatBoxLabel>
                      {convertTimeToMMSS(Math.round(seg.duration))}
                    </BeatBoxLabel>
                  </BeatBox>
                </React.Fragment>
              );
            })}
          </BeatTimeline>
        </VideoOverlayControls>
      </VideoSection>

      <ChapterStrip>
        {hasClips ? (
          chapterGroups.map((group, groupIdx) => {
            const chapterIdx = group.segments[0]?.seg.chapterIndex ?? groupIdx;
            const chapterKey = String(chapterIdx);
            return (
              <ChapterGroup
                key={groupIdx}
                $isDragging={chapterDragKey === chapterKey}
                $isDragOver={chapterDragOverKey === chapterKey}
                onDragOver={e => handleChapterDragOver(e, chapterIdx)}
                onDragLeave={handleChapterDragLeave}
                onDrop={e => handleChapterDrop(e, chapterIdx)}
              >
                <ChapterLabelRow>
                  <ChapterGrip
                    draggable
                    onDragStart={e => handleChapterDragStart(e, chapterIdx)}
                    onDragEnd={handleChapterDragEnd}
                  >
                    ⠿
                  </ChapterGrip>
                  <ChapterLabel>{group.title}</ChapterLabel>
                </ChapterLabelRow>
                <ThumbnailRow>
                  {group.segments.map(({ seg, globalIdx }) => {
                    const beatDragId = `${seg.chapterIndex}.${seg.beatIndex}`;
                    return (
                      <ThumbnailCard
                        key={seg.beatKey}
                        $active={globalIdx === currentSegmentIndex}
                        $isDragging={beatDragKey === beatDragId}
                        $isDragOver={beatDragOverKey === beatDragId}
                        onClick={() => seekToSegment(globalIdx)}
                        onDragOver={e =>
                          handleBeatDragOver(e, seg.chapterIndex, seg.beatIndex)
                        }
                        onDragLeave={handleBeatDragLeave}
                        onDrop={e =>
                          handleBeatDrop(e, seg.chapterIndex, seg.beatIndex)
                        }
                      >
                        {seg.thumbnailUrl && (
                          <ThumbnailImg
                            src={seg.thumbnailUrl}
                            alt={seg.title}
                          />
                        )}
                        <ThumbnailPlayIcon>&#9654;</ThumbnailPlayIcon>
                        <ThumbnailGrip
                          draggable
                          onDragStart={e =>
                            handleBeatDragStart(
                              e,
                              seg.chapterIndex,
                              seg.beatIndex,
                            )
                          }
                          onDragEnd={handleBeatDragEnd}
                        >
                          ⠿
                        </ThumbnailGrip>
                      </ThumbnailCard>
                    );
                  })}
                </ThumbnailRow>
              </ChapterGroup>
            );
          })
        ) : (
          <EmptyStrip>No clips to preview</EmptyStrip>
        )}
      </ChapterStrip>

      <Tooltip
        id="preview-tooltip"
        place="top"
        style={{
          maxWidth: '350px',
          fontSize: '11px',
          lineHeight: '1.5',
          zIndex: 100001,
          borderRadius: '6px',
          padding: '8px 12px',
        }}
      />
    </Container>
  );
};

ReelPreviewPlayer.displayName = 'ReelPreviewPlayer';

export default ReelPreviewPlayer;

// --- Styled Components ---

const Container = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  background: var(--background);
  padding: 12px;
  gap: 8px;
`;

const VideoSection = styled.div`
  position: relative;
  flex: 1 1 0;
  min-height: 0;
  border-radius: 8px;
  overflow: hidden;
  background: #000;
`;

const NoVideoMessage = styled.div`
  color: var(--text-muted);
  font-size: 13px;
`;

const VideoWrapper = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;

const StyledVideo = styled.video`
  width: 100%;
  height: 100%;
  object-fit: contain;
  pointer-events: none;

  &::-webkit-media-controls {
    display: none !important;
  }
  &::-webkit-media-controls-enclosure {
    display: none !important;
  }
`;

const ClipInfoOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 10px 12px 16px;
  background: linear-gradient(
    rgba(0, 0, 0, 0.8),
    rgba(0, 0, 0, 0.5) 70%,
    transparent
  );
  pointer-events: none;
  z-index: 1;
`;

const ClipInfoHeader = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
`;

const ClipInfoTitle = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: #fff;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ClipInfoTime = styled.span`
  font-size: 10px;
  color: rgba(255, 255, 255, 0.6);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  flex-shrink: 0;
`;

const ClipInfoTranscript = styled.div`
  font-size: 11px;
  color: rgba(255, 255, 255, 0.75);
  line-height: 1.5;
  max-height: 60px;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
`;

const VideoOverlayControls = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 8px 10px 6px;
  background: linear-gradient(
    transparent,
    rgba(0, 0, 0, 0.7) 30%,
    rgba(0, 0, 0, 0.85)
  );
  pointer-events: auto;
`;

const OverlayControlsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 4px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 2px;
  cursor: pointer;
  position: relative;

  &:hover {
    height: 6px;
  }
`;

const ProgressFill = styled.div`
  height: 100%;
  background: var(--accent, #4ade80);
  border-radius: 2px;
  transition: width 100ms linear;
`;

const PlayPauseButton = styled.button`
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 50%;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 150ms ease-out;

  &:hover:not(:disabled) {
    background-color: rgba(255, 255, 255, 0.1);
    transform: scale(1.05);
  }

  &:active:not(:disabled) {
    transform: scale(0.95);
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const ControlButton = styled.button`
  border: none;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px;
  border-radius: var(--radius-button, 4px);
  cursor: pointer;
  flex-shrink: 0;
  transition: all 150ms ease-out;
  opacity: 0.7;

  &:hover:not(:disabled) {
    background-color: rgba(255, 255, 255, 0.1);
    opacity: 1;
  }

  &:active:not(:disabled) {
    transform: scale(0.95);
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
    pointer-events: none;
  }
`;

const ControlBackward = styled.span`
  display: flex;
  transform: rotate(180deg);
`;

const TimeDisplay = styled.span`
  font-size: 11px;
  color: rgba(255, 255, 255, 0.8);
  font-variant-numeric: tabular-nums;
  flex-shrink: 0;
`;

const NowPlaying = styled.span`
  font-size: 11px;
  color: rgba(255, 255, 255, 0.6);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
  flex: 1;
  text-align: right;
`;

const BeatTimeline = styled.div`
  display: flex;
  align-items: stretch;
  height: 24px;
  border-radius: 3px;
  overflow: hidden;
  gap: 1px;
`;

const ChapterDivider = styled.div`
  width: 2px;
  background: rgba(255, 255, 255, 0.3);
  flex-shrink: 0;
`;

const BeatBox = styled.div<{
  $widthPercent: number;
  $active: boolean;
  $isDragging?: boolean;
  $isDragOver?: boolean;
}>`
  flex: ${props => props.$widthPercent} 0 0;
  min-width: 16px;
  background: ${props =>
    props.$active ? 'var(--accent, #4ade80)' : 'rgba(255, 255, 255, 0.15)'};
  cursor: grab;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 3px;
  overflow: hidden;
  transition:
    background 150ms ease,
    opacity 150ms ease;
  opacity: ${props => (props.$isDragging ? 0.4 : 1)};
  box-shadow: ${props =>
    props.$isDragOver ? '0 -2px 0 0 var(--primary) inset' : 'none'};

  &:hover {
    background: ${props =>
      props.$active ? 'var(--accent, #4ade80)' : 'rgba(255, 255, 255, 0.3)'};
  }

  &:active {
    cursor: grabbing;
  }
`;

const BeatBoxLabel = styled.span`
  font-size: 8px;
  color: rgba(255, 255, 255, 0.8);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

// --- Chapter Strip ---

const ChapterStrip = styled.div`
  display: flex;
  gap: 16px;
  overflow-x: auto;
  flex-shrink: 0;
  padding: 4px 0;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const ChapterGroup = styled.div<{
  $isDragging?: boolean;
  $isDragOver?: boolean;
}>`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex-shrink: 0;
  opacity: ${props => (props.$isDragging ? 0.4 : 1)};
  border-left: ${props =>
    props.$isDragOver ? '2px solid var(--primary)' : '2px solid transparent'};
  transition: opacity 150ms ease;
`;

const ChapterLabelRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const ChapterGrip = styled.span`
  font-size: 14px;
  line-height: 1;
  color: var(--text-muted);
  opacity: 0.4;
  cursor: grab;
  user-select: none;
  transition: opacity 150ms ease;

  &:hover {
    opacity: 1;
  }

  &:active {
    cursor: grabbing;
  }
`;

const ChapterLabel = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: var(--foreground);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 200px;
`;

const ThumbnailRow = styled.div`
  display: flex;
  gap: 6px;
`;

const ThumbnailCard = styled.div<{
  $active: boolean;
  $isDragging?: boolean;
  $isDragOver?: boolean;
}>`
  position: relative;
  width: 100px;
  height: 56px;
  border-radius: 4px;
  overflow: hidden;
  cursor: pointer;
  flex-shrink: 0;
  border: 2px solid
    ${props => (props.$active ? 'var(--accent)' : 'transparent')};
  transition:
    border-color 150ms ease,
    opacity 150ms ease;
  opacity: ${props => (props.$isDragging ? 0.4 : 1)};
  border-top: ${props =>
    props.$isDragOver ? '2px solid var(--primary)' : undefined};

  &:hover {
    border-color: ${props =>
      props.$active ? 'var(--accent)' : 'var(--border)'};
  }
`;

const ThumbnailImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const ThumbnailPlayIcon = styled.span`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 16px;
  background: rgba(0, 0, 0, 0.3);
  filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.5));
`;

const ThumbnailGrip = styled.span`
  position: absolute;
  top: 2px;
  left: 4px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
  line-height: 1;
  user-select: none;
  cursor: grab;
  opacity: 0;
  transition: opacity 150ms ease;

  ${ThumbnailCard}:hover & {
    opacity: 1;
  }

  &:active {
    cursor: grabbing;
  }
`;

const EmptyStrip = styled.div`
  font-size: 12px;
  color: var(--text-muted);
  padding: 8px 0;
`;
